// Icons inline SVG — não dependemos de lucide-react ou similares pra reduzir bundle.
// Cada icon recebe `size`, `strokeWidth` e qualquer prop SVG comum.
// Replicado de _anexos/design_v1/app/icons.jsx mantendo viewBox 24x24.
import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'ref'> {
  size?: number;
  strokeWidth?: number;
}

function makeIcon(
  displayName: string,
  paths: (props: Required<Pick<IconProps, 'strokeWidth'>>) => React.ReactNode,
) {
  function Icon({ size = 18, strokeWidth = 2, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...rest}
      >
        {paths({ strokeWidth })}
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export const ChatIcon = makeIcon('ChatIcon', () => (
  <path d="M3 12a9 9 0 1 1 4 7.5L3 21l1.5-4A9 9 0 0 1 3 12Z" />
));

export const CalendarIcon = makeIcon('CalendarIcon', () => (
  <>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </>
));

// Alvo — três círculos concêntricos. Usado no rail/rota "Metas".
export const TargetIcon = makeIcon('TargetIcon', () => (
  <>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </>
));

export const TrophyIcon = makeIcon('TrophyIcon', () => (
  <>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </>
));

// Coroa — marca a 1ª posição no pódium. Renderizada preenchida (passar
// fill="currentColor") na cor ouro; decorativa (aria-hidden herdado do makeIcon).
export const CrownIcon = makeIcon('CrownIcon', () => (
  <path d="M3 7l4 4 5-6 5 6 4-4-1.6 11H4.6L3 7Z" />
));

export const InboxIcon = makeIcon('InboxIcon', () => (
  <>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
  </>
));

export const HomeIcon = makeIcon('HomeIcon', () => (
  <>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-4h-2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
  </>
));

export const QueueIcon = makeIcon('QueueIcon', () => (
  <>
    <circle cx="12" cy="6" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="18" r="2" />
  </>
));

export const StoreIcon = makeIcon('StoreIcon', () => (
  <>
    <path d="M3 7l1.5-3h15L21 7" />
    <path d="M3 7v3a3 3 0 0 0 6 0v0a3 3 0 0 0 6 0v0a3 3 0 0 0 6 0V7" />
    <path d="M5 12v8h14v-8" />
  </>
));

export const TeamIcon = makeIcon('TeamIcon', () => (
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>
));

export const AnalyticsIcon = makeIcon('AnalyticsIcon', () => (
  <>
    <path d="M3 21V3" />
    <path d="M21 21H3" />
    <rect x="6" y="10" width="3" height="9" />
    <rect x="11" y="6" width="3" height="13" />
    <rect x="16" y="13" width="3" height="6" />
  </>
));

export const AiIcon = makeIcon('AiIcon', () => (
  <>
    <path d="M12 3l1.5 3.5L17 8l-3.5 1.5L12 13l-1.5-3.5L7 8l3.5-1.5L12 3Z" />
    <path d="M19 14l.75 1.75L21.5 16.5l-1.75.75L19 19l-.75-1.75L16.5 16.5l1.75-.75L19 14Z" />
  </>
));

export const SettingsIcon = makeIcon('SettingsIcon', () => (
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.86l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.86-.34 1.7 1.7 0 0 0-1.04 1.55V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.5 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.33-1.87l-.06-.06A2 2 0 1 1 7.04 4.3l.06.06A1.7 1.7 0 0 0 9 4.7h.05A1.7 1.7 0 0 0 10 3.1V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5h.05a1.7 1.7 0 0 0 1.86-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9v.05a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.05a1.7 1.7 0 0 0-1.55 1Z" />
  </>
));

export const LogoutIcon = makeIcon('LogoutIcon', () => (
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </>
));

export const ArrowRightIcon = makeIcon('ArrowRightIcon', () => (
  <>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </>
));

export const ArrowLeftIcon = makeIcon('ArrowLeftIcon', () => (
  <>
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </>
));

export const CheckIcon = makeIcon('CheckIcon', () => <path d="M20 6 9 17l-5-5" />);

export const CopyIcon = makeIcon('CopyIcon', () => (
  <>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </>
));

export const EyeIcon = makeIcon('EyeIcon', () => (
  <>
    <path d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8Z" />
    <circle cx="12" cy="12" r="3" />
  </>
));

export const EyeOffIcon = makeIcon('EyeOffIcon', () => (
  <>
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-10-8-10-8a18 18 0 0 1 5.17-6.07" />
    <path d="M9.9 4.24A10 10 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <path d="M1 1l22 22" />
  </>
));

export const PlusIcon = makeIcon('PlusIcon', () => (
  <>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </>
));

export const TrashIcon = makeIcon('TrashIcon', () => (
  <>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </>
));

export const EditIcon = makeIcon('EditIcon', () => (
  <>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" />
  </>
));

export const SearchIcon = makeIcon('SearchIcon', () => (
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-3.5-3.5" />
  </>
));

export const ShieldIcon = makeIcon('ShieldIcon', () => (
  <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z" />
));

export const ZapIcon = makeIcon('ZapIcon', () => <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" />);

export const RefreshIcon = makeIcon('RefreshIcon', () => (
  <>
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </>
));

export const UserPlusIcon = makeIcon('UserPlusIcon', () => (
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M20 8v6" />
    <path d="M23 11h-6" />
  </>
));

export const ChevronRightIcon = makeIcon('ChevronRightIcon', () => <path d="m9 6 6 6-6 6" />);

export const PhoneIcon = makeIcon('PhoneIcon', () => (
  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.87.32 1.71.6 2.52a2 2 0 0 1-.45 2.11L8.09 9.92a16 16 0 0 0 6 6l1.57-1.18a2 2 0 0 1 2.11-.45c.81.28 1.65.48 2.52.6A2 2 0 0 1 22 16.92Z" />
));

export const SparkleIcon = makeIcon('SparkleIcon', () => (
  <>
    <path d="M12 2v6" />
    <path d="M12 16v6" />
    <path d="M2 12h6" />
    <path d="M16 12h6" />
  </>
));

export const SparklesIcon = makeIcon('SparklesIcon', () => (
  <>
    <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2Z" />
    <path d="M19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9L19 14Z" />
  </>
));

export const StarIcon = makeIcon('StarIcon', () => (
  <path d="M12 2l3 6.5 7 .9-5 4.7 1.3 7-6.3-3.5L5.7 21 7 14.1l-5-4.7 7-.9L12 2Z" />
));

export const TrendingUpIcon = makeIcon('TrendingUpIcon', () => (
  <>
    <path d="m3 17 6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </>
));

export const TrendingDownIcon = makeIcon('TrendingDownIcon', () => (
  <>
    <path d="m3 7 6 6 4-4 8 8" />
    <path d="M14 17h7v-7" />
  </>
));

export const FileTextIcon = makeIcon('FileTextIcon', () => (
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8" />
    <path d="M8 17h6" />
  </>
));

export const BotIcon = makeIcon('BotIcon', () => (
  <>
    <rect x="3" y="8" width="18" height="12" rx="3" />
    <path d="M12 2v4" />
    <circle cx="8.5" cy="14" r="1" />
    <circle cx="15.5" cy="14" r="1" />
  </>
));

export const FlameIcon = makeIcon('FlameIcon', () => (
  <path d="M8.5 14.5c0-3 4-4 3-7.5-.5-1.7 0-4 2.5-5 0 4 5 6 5 11a6 6 0 1 1-12 0c0-1 .5-2 1.5-3Z" />
));

export const HeartIcon = makeIcon('HeartIcon', () => (
  <path d="M12 21s-7-4.5-9.5-9A5 5 0 0 1 12 6a5 5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9Z" />
));

export const ClockIcon = makeIcon('ClockIcon', () => (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>
));

export const PackageIcon = makeIcon('PackageIcon', () => (
  <>
    <path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </>
));

export const UsersIcon = makeIcon('UsersIcon', () => (
  <>
    <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9.5" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>
));

export const BriefcaseIcon = makeIcon('BriefcaseIcon', () => (
  <>
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </>
));

export const XIcon = makeIcon('XIcon', () => (
  <>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </>
));

export const DownloadIcon = makeIcon('DownloadIcon', () => (
  <>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
    <path d="M12 15V3" />
  </>
));

export const AlertTriangleIcon = makeIcon('AlertTriangleIcon', () => (
  <>
    <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </>
));

/** Sucesso — círculo com check. Par do AlertCircleIcon nos toasts (RF-04). */
export const CheckCircleIcon = makeIcon('CheckCircleIcon', () => (
  <>
    <path d="M21.8 10.9V12a10 10 0 1 1-5.93-9.14" />
    <path d="M22 4 12 14.01l-3-3" />
  </>
));

/** Erro — círculo com exclamação. Distinto do AlertTriangleIcon, que é aviso. */
export const AlertCircleIcon = makeIcon('AlertCircleIcon', () => (
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </>
));

export const SunIcon = makeIcon('SunIcon', () => (
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </>
));

export const MoonIcon = makeIcon('MoonIcon', () => (
  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
));

export const MonitorIcon = makeIcon('MonitorIcon', () => (
  <>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </>
));

export const PowerIcon = makeIcon('PowerIcon', () => (
  <>
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <path d="M12 2v10" />
  </>
));

export const BellIcon = makeIcon('BellIcon', () => (
  <>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </>
));

export const MessageSquareIcon = makeIcon('MessageSquareIcon', () => (
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
));

// Info — círculo com "i". Abre o painel do cliente no inbox mobile (ThreadHeader).
export const InfoIcon = makeIcon('InfoIcon', () => (
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </>
));

export const FileIcon = makeIcon('FileIcon', () => (
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" />
    <path d="M14 2v6h6" />
  </>
));

export const VideoIcon = makeIcon('VideoIcon', () => (
  <>
    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
    <rect x="2" y="6" width="14" height="12" rx="2" />
  </>
));

export const SendIcon = makeIcon('SendIcon', () => (
  <>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22 11 13 2 9 22 2Z" />
  </>
));

export const MicIcon = makeIcon('MicIcon', () => (
  <>
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <path d="M12 19v3" />
  </>
));

export const ImageIcon = makeIcon('ImageIcon', () => (
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </>
));

export const PaperclipIcon = makeIcon('PaperclipIcon', () => (
  <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l8.57-8.57a4 4 0 0 1 5.66 5.66l-8.58 8.58a2 2 0 0 1-2.83-2.83l8.49-8.48" />
));

export const MoreVerticalIcon = makeIcon('MoreVerticalIcon', () => (
  <>
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </>
));

// Reticências horizontais — botão "Mais" da BottomNav (overflow de navegação).
export const MoreHorizontalIcon = makeIcon('MoreHorizontalIcon', () => (
  <>
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </>
));

export const CatalogIcon = makeIcon('CatalogIcon', () => (
  <>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </>
));

export const UserIcon = makeIcon('UserIcon', () => (
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
  </>
));

/** Figurinha (menu Anexar do chat). */
export const SmileIcon = makeIcon('SmileIcon', () => (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <path d="M9 9h.01M15 9h.01" />
  </>
));

export const HandoffIcon = makeIcon('HandoffIcon', () => (
  <>
    <path d="M7 7H4l3-3M4 7l3 3" />
    <path d="M17 17h3l-3 3M20 17l-3-3" />
    <path d="M7 7h6a4 4 0 0 1 4 4v6" />
  </>
));

export const MailIcon = makeIcon('MailIcon', () => (
  <>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m4 7 8 6 8-6" />
  </>
));

export const LinkIcon = makeIcon('LinkIcon', () => (
  <>
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
  </>
));

export const LockIcon = makeIcon('LockIcon', () => (
  <>
    <rect x="4" y="10" width="16" height="11" rx="2.5" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </>
));

export const GoogleIcon = makeIcon('GoogleIcon', () => (
  <>
    <path
      d="M21 12c0-.7-.1-1.4-.2-2H12v3.8h5.1c-.2 1.2-.9 2.2-1.9 2.9v2.4h3.1c1.8-1.7 2.7-4.1 2.7-7.1Z"
      fill="#4285F4"
      stroke="none"
    />
    <path
      d="M12 21c2.6 0 4.8-.9 6.3-2.3l-3.1-2.4c-.9.6-2 .9-3.2.9-2.5 0-4.6-1.6-5.3-3.9H3.5v2.4C5.1 18.7 8.3 21 12 21Z"
      fill="#34A853"
      stroke="none"
    />
    <path
      d="M6.7 13.3a5 5 0 0 1 0-2.6V8.3H3.5a9 9 0 0 0 0 7.4l3.2-2.4Z"
      fill="#FBBC04"
      stroke="none"
    />
    <path
      d="M12 6.6c1.4 0 2.6.5 3.6 1.4l2.7-2.7C16.8 3.9 14.6 3 12 3 8.3 3 5.1 5.3 3.5 8.3l3.2 2.4C7.4 8.2 9.5 6.6 12 6.6Z"
      fill="#EA4335"
      stroke="none"
    />
  </>
));
